/**
 * Compresses and resizes an image file.
 * @param {File} file - The image file to compress.
 * @param {number} maxWidth - Maximum width of the output image.
 * @param {number} maxHeight - Maximum height of the output image.
 * @param {number} quality - JPEG quality (0 to 1).
 * @returns {Promise<Blob>} - A promise that resolves to the compressed image Blob.
 */
export const compressImage = (file, maxWidth = 600, maxHeight = 600, quality = 0.7) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                // Calculate new dimensions while maintaining aspect ratio
                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Canvas to Blob conversion failed'));
                        }
                    },
                    'image/jpeg',
                    quality
                );
            };
            img.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
    });
};

/**
 * Extracts a clean filename from a URL.
 * Removes URL encoding, query parameters, and timestamp prefixes.
 * @param {string} url - The image URL.
 * @returns {string} - The cleaned filename.
 */
export const getFileName = (url) => {
    if (!url) return '';
    try {
        // Handle full URLs
        if (url.startsWith('http')) {
            const urlObj = new URL(url);
            const pathname = decodeURIComponent(urlObj.pathname);
            let name = pathname.split('/').pop();
            name = name.replace(/^\d+_/, '');
            return name;
        }

        // Handle simple paths or filenames
        let name = decodeURIComponent(url).split('/').pop().split('?')[0];
        name = name.replace(/^\d+_/, '');
        return name;
    } catch (e) {
        return 'image.jpg';
    }
};
